import type { InternationalTableRowsProperties } from '.'

// CAS and the international table can spell the larger sizes either way
const SIZE_ALIASES: Record<string, string> = {
  '2XL': 'XXL',
  '3XL': 'XXXL'
}

export const normaliseSize = (size: string | number) => {
  const value = String(size).trim().toUpperCase()
  return SIZE_ALIASES[value] || value
}

const findSizeIndex = (values: (string | number)[], size: string) =>
  values.findIndex(value => normaliseSize(value) === normaliseSize(size))

// Any row can hold the size labels, e.g. The Jack leads with chest size ahead of standard size
export const findInternationalSizeIndex = (
  rows: InternationalTableRowsProperties[],
  size: string
) => {
  for (const { values } of rows) {
    const index = findSizeIndex(values, size)

    if (index !== -1) return index
  }

  return -1
}

// Shown for a product size the international table has no conversion for yet
export const MISSING_SIZE_PLACEHOLDER = '–'

const getProductColumns = (
  rows: InternationalTableRowsProperties[],
  sizes: string[]
) => {
  for (let sizeRowIndex = 0; sizeRowIndex < rows.length; sizeRowIndex++) {
    const indexes = sizes.map(size =>
      findSizeIndex(rows[sizeRowIndex].values, size)
    )

    if (indexes.some(index => index !== -1)) return { sizeRowIndex, indexes }
  }

  return null
}

/**
 * Lines the international table up with the sizes the product comes in, as products
 * don't all share the table's size range (e.g. XS-XXXL vs S-XXL). Sizes the table
 * doesn't have yet keep their column with a placeholder. Falls back to the full
 * table, less any columns without a size label, when nothing matches.
 */
export const getProductInternationalTableRows = (
  rows: InternationalTableRowsProperties[],
  sizes: string[]
): InternationalTableRowsProperties[] => {
  const productColumns = getProductColumns(rows, sizes)

  if (productColumns) {
    const { sizeRowIndex, indexes } = productColumns

    return rows.map(({ name, values }, rowIndex) => ({
      name,
      values: indexes.map((index, i) => {
        if (index !== -1) return values[index] ?? ''

        return rowIndex === sizeRowIndex
          ? normaliseSize(sizes[i])
          : MISSING_SIZE_PLACEHOLDER
      })
    }))
  }

  const labelledIndexes = (rows[0]?.values || [])
    .map((value, index) => (String(value).trim() ? index : -1))
    .filter(index => index !== -1)

  return rows.map(({ name, values }) => ({
    name,
    values: labelledIndexes.map(index => values[index] ?? '')
  }))
}
